import { z } from 'zod';
import { getEditorReactComponentExtensionsDir } from '@wix/cli-astro-definitions';
import {
  VALID_PATH_REGEX,
  folderExistsMessage,
  pathAvailableValidation,
} from '../../../dev-center/components/validations/common.js';
import type { ExtensionDef } from '../generate-schema/types.js';
import { defineField } from '../generate-schema/definitions.js';
import { enrichWithDerivedFolder } from '../utils/derive-folder.js';
import type { WithDerivedFolder } from '../utils/derive-folder.js';
import { generateEditorReactComponent } from './generate.js';

const schema = z.object({
  extensionType: z.literal('EDITOR_REACT_COMPONENT'),
  name: z
    .string()
    .max(30)
    .describe(defineField({ title: 'Component name' })),
  folder: z
    .string()
    .regex(VALID_PATH_REGEX)
    .optional()
    .describe(
      defineField({
        title: 'Component folder',
        description:
          'Lowercase alphanumeric and hyphens only. Derived from the component name when omitted.',
      })
    ),
  description: z
    .string()
    .min(3)
    .max(300)
    .describe(defineField({ title: 'Component description' })),
});

export const editorReactComponent: ExtensionDef<
  typeof schema,
  WithDerivedFolder<typeof schema>
> = {
  title: 'Editor React Component',
  description: 'A site widget built as a React component for the Wix editor',
  group: 'Site',
  zodSchema: schema,
  enrich: (data) => enrichWithDerivedFolder(data, { sourceField: 'name' }),
  validate: async (projectFolder, data) => {
    const isAvailable = pathAvailableValidation(
      getEditorReactComponentExtensionsDir(projectFolder)
    )(data.folder);
    return isAvailable
      ? null
      : folderExistsMessage('component', data.folder, 'name');
  },
  generate: ({ projectFolder, model, data }) =>
    generateEditorReactComponent(
      projectFolder,
      data.folder,
      data.name,
      data.description,
      model.config.projectType === 'App'
        ? model.config.codeIdentifier
        : undefined
    ),
};
